/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.USER.LIST
 *
 * Acumatica-style high-density grid for user maintenance.
 * Features:
 *  - Persistent text-search filter (sessionStorage)
 *  - Inline activate/deactivate toggle (Y/N -> Yes/No)
 *  - Edit drawer for display_name, locale, role assignment
 *  - Role assignment via FilteredModalMultiSelect (auto-search > 10 options)
 *  - Forensic stamping of modified_by using getForensicActorId()
 */
/* tl-group-ok: the listed row is a csm_user_master, which has no _tl — a person is not
   translated. The embedded csm_role_master_tl / csm_job_description_tl resolve the NAMES of
   a user's related roles and job description (§17.B4); those belong to the role and the job,
   and would be nonsense listed as translations of the user. */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { fetchAllRows } from '../../lib/fetch_all_rows';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate, routedInsert, routedInsertMany } from '../../lib/routed_writes';
import { hasAccess, isSuperAdmin, type AuthSession } from '../../types/auth';
import { collectReportingSubtree } from '../../lib/reporting_line';
import { useCanDeactivateUser } from '../../lib/use_can_deactivate_user';
import { registerChildEditor, type ChildEditorProps } from '../../lib/child_editors';
import { FilteredModalMultiSelect, type MultiSelectOption } from '../../components/common/FilteredModalMultiSelect';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { LocaleSelect } from '../../components/common/LocaleSelect';
import { localeName } from '../../i18n/locale_display';
import { Modal } from '../../components/common/Modal';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { compareStrings, useSort } from '../../lib/use_sort';
import { useOrgLookups } from '../../lib/use_org_lookups';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { SmartQueryPopover } from '../../components/ai/SmartQueryPopover';
import { SmartPasteDropzone } from '../../components/ai/SmartPasteDropzone';
import { useAiAssist } from '../../lib/ai/use_ai_assist';
import { applyAiQuery } from '../../lib/ai/grid_filter';
import type { AiColumnSchema, AiFieldSchema, AiQuery } from '../../lib/ai/types';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef, FieldLayoutItem } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { RowActionsMenu } from '../../components/common/RowActionsMenu';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { useRecordExtensions } from '../../hooks/use_record_extensions';
import { useRecordExtensionForm } from '../../hooks/use_record_extension_form';
import { ExtensionFacets } from '../../components/common/ExtensionFacets';
import { RecordExtensionFields } from '../../components/common/RecordExtensionFields';

type UserSortKey =
  | 'email' | 'display_name' | 'job_description_label'
  | 'preferred_locale' | 'role_codes' | 'is_active' | 'is_admin';

interface UserRow {
  user_id: number;
  internal_code: string;
  email: string;
  display_name: string;
  preferred_locale: string;
  is_admin: 'Y' | 'N';
  is_active: 'Y' | 'N';
  can_deactivate_users: 'Y' | 'N';
  can_mimic_users: 'Y' | 'N';
  job_description_id: number | null;
  job_description_label: string;
  manager_user_id: number | null;
  manager_label: string;
  company_id: number | null;
  division_id: number | null;
  business_unit_id: number | null;
  office_id: number | null;
  role_codes: string[];
  can_login: boolean;   // derived: a Supabase login exists for this email and is enabled
  has_login: boolean;   // a Supabase login RECORD exists (enabled or banned)
}


interface RoleRow {
  role_id: number;
  internal_code: string;
  display_text: string;
  is_active: 'Y' | 'N';
}

interface JobOpt {
  job_description_id: number;
  internal_code: string;
  display_text: string;
}

// Related-children (expand/contract detail panel) refs - keyed by user_id.
// Names are resolved through _tl; the raw code/email rides along in `title` (§17.B4).
interface UserRoleRef {
  role_id: number;
  code: string;   // role internal_code (hover title)
  name: string;   // resolved role name (label)
}
interface DirectReportRef {
  user_id: number;
  name: string;   // display_name (label)
  email: string;  // hover title
}
/** A page the user can reach (via their roles) + the permissions they hold on it. */
interface PageAccessRef {
  page_id: number;
  label: string;   // resolved page name (§17.B4)
  code: string;    // page internal_code (hover title)
  perms: string[]; // unioned V/A/C/D/S across the user's roles
}

const PERM_TITLES: Record<string, string> = { V: 'View', A: 'Add', C: 'Change', D: 'Delete', S: 'Sign-off' };
const PERM_ORDER = ['V', 'A', 'C', 'D', 'S'];

const FILTER_KEY = 'csm.users.filter';

// Internal_code of this screen — keys both the AI screen config and the audit log.
const PAGE_CODE = 'CSM.USER.LIST';
registerUdcHost(PAGE_CODE, 'csm_user_master', 'Users');   // host for user custom fields

// Schema the Smart Query (NLQ) engine is allowed to reference. The csm-ai
// gateway validates its output against exactly these column keys, so the model
// cannot invent fields. Boolean columns are stored Y/N at this layer.
const AI_GRID_SCHEMA: AiColumnSchema[] = [
  { key: 'email',                 label: 'Email',          type: 'string' },
  { key: 'display_name',          label: 'Name',           type: 'string' },
  { key: 'job_description_label', label: 'Job',            type: 'string' },
  { key: 'preferred_locale',      label: 'Locale',         type: 'string' },
  { key: 'role_codes',            label: 'Roles',          type: 'string' },
  { key: 'is_active',             label: 'Active (Y/N)',   type: 'boolean' },
  { key: 'is_admin',              label: 'Admin (Y/N)',    type: 'boolean' },
];

// Form fields the Smart Paste utility may pre-fill on the New User drawer.
const AI_NEW_USER_FIELDS: AiFieldSchema[] = [
  { key: 'email',            label: 'Email',        type: 'string', required: true },
  { key: 'display_name',     label: 'Display name', type: 'string' },
  { key: 'preferred_locale', label: 'Locale',       type: 'string' },
];

const GRID_COLS: GridColumnDef[] = [
  { key: 'email',                 header: 'Email',   defaultWidth: 220 },
  { key: 'display_name',          header: 'Name',    defaultWidth: 160 },
  { key: 'job_description_label', header: 'Job',     defaultWidth: 150 },
  { key: 'preferred_locale',      header: 'Locale',  defaultWidth: 90  },
  { key: 'role_codes',            header: 'Roles',   defaultWidth: 180 },
  { key: 'is_active',             header: 'Active',  defaultWidth: 80  },
  { key: 'is_admin',              header: 'Admin',   defaultWidth: 80  },
  { key: 'can_login',             header: 'Login',   defaultWidth: 80  },
];

export interface UserListPageProps {
  session: AuthSession;
  onBeginMimic?: (userId: number) => void;
}

export function UserListPage({ session, onBeginMimic }: UserListPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <UserListPageInner session={session} onBeginMimic={onBeginMimic} />
    </PersonalizationProvider>
  );
}

function UserListPageInner({ session, onBeginMimic }: UserListPageProps) {
  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [jobs, setJobs]   = useState<JobOpt[]>([]);
  // Related-children (expand/contract detail panel) - keyed by user_id.
  const [rolesByUser, setRolesByUser]     = useState<Map<number, UserRoleRef[]>>(new Map());
  const [reportsByUser, setReportsByUser] = useState<Map<number, DirectReportRef[]>>(new Map());
  const [pagesByUser, setPagesByUser]     = useState<Map<number, PageAccessRef[]>>(new Map());
  const detail = useExpandedRows<number>();
  const [filter, setFilter] = useState<string>(
    () => sessionStorage.getItem(FILTER_KEY) ?? '',
  );
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ job: string[]; locale: string[]; role: string[]; active: string[]; admin: string[] }>(
    { job: [], locale: [], role: [], active: ['Y'], admin: [] },
  );
  const sort = useSort<UserSortKey>('csm.users.sort', { key: 'display_name', dir: 'asc' });
  const { config: aiConfig } = useAiAssist(PAGE_CODE);
  const [aiQuery, setAiQuery] = useState<AiQuery | null>(null);
  const orgLookups = useOrgLookups(activeLocale);
  // §17.B1/B4: resolve FK codes to translated names for display (never the code).
  // A language is named by localeName like everywhere else, not by its _tl row —
  // see lib/language_options_core.
  // useCallback so the facet memo below can depend on it honestly: as a bare
  // arrow it was a new identity every render, and the memo listed activeLocale
  // (the only thing it closes over) instead — correct, but invisible to the
  // exhaustive-deps rule.
  const localeNameOf = useCallback(
    (code: string) => localeName(code, activeLocale),
    [activeLocale],
  );
  const roleNames = useMemo(() => Object.fromEntries(roles.map(r => [r.internal_code, r.display_text])), [roles]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creating, setCreating]   = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionTarget, setActionTarget] = useState<UserRow | null>(null);
  const [actionType, setActionType] = useState<'deactivate' | 'activate' | null>(null);

  // §6 RBAC: the csm_role_page_access matrix decides which write affordances
  // this user gets. A view-only role must not be offered Edit / activate /
  // New User at all. The server refuses these writes regardless (RLS, via the
  // Common Routine — see 20260823000000_csm_common_routine_enforce_rls.sql);
  // this keeps the UI from offering an action that can only end in an error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  // Mimic and activate/deactivate are no longer page verbs at all — they are
  // per-TARGET capabilities (20260826000000_csm_delegated_deactivate_mimic).
  // A CSM.SUPER_ADMIN may act on anyone; everyone else needs the matching flag
  // on their own record AND the target beneath them in the reporting line.
  //
  // Mirrors the server (csm_can_mimic_user / csm_can_deactivate_user) so the UI
  // stops offering an action that can only end in an error. The server, not
  // this, is the authorization decision — see the note on UserContext's flags
  // about mimic swapping the effective user out from under them.
  const isSuper = isSuperAdmin(session);
  // Every user beneath the signed-in user, from the rows already on screen.
  const mySubtree = useMemo(
    () => collectReportingSubtree(users, session.effective_user.user_id),
    [users, session.effective_user.user_id],
  );
  const canDeactivateUser = (u: UserRow) =>
    isSuper || (session.effective_user.can_deactivate_users && mySubtree.has(u.user_id));
  const canMimicUser = (u: UserRow) =>
    isSuper || (session.effective_user.can_mimic_users && mySubtree.has(u.user_id));

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const locale = activeLocale;

      // Users + their currently-active role assignments + job description.
      //
      // Disambiguation:
      //   csm_user_master <-> csm_user_role_assignment : two FKs - pin via fk_ura_user.
      //   csm_user_master <-> csm_job_description       : two FKs - pin via fk_um_job.
      //
      // Note: the manager (self-FK on csm_user_master) is fetched in a separate
      // query below. Self-referencing FK embeds via PostgREST can fail to
      // resolve in some Supabase configurations even with the constraint
      // name pinned, so we side-step the issue with a tiny second round-trip.
      // The list builds client-side role/report/page maps and an AI-query filter
      // over the whole user set, so it must load every user — range-page past the
      // row cap (display_name is not unique, so user_id is the stable tiebreaker).
      const rawUsers = await fetchAllRows((from, to) => apiDb
        .from('csm_user_master')
        .select(`
          user_id, internal_code, email, display_name, preferred_locale,
          is_admin, is_active, can_deactivate_users, can_mimic_users,
          job_description_id, manager_user_id,
          company_id, division_id, business_unit_id, office_id,
          job:csm_job_description!fk_csm_um_job_description_id ( internal_code,
            csm_job_description_tl ( locale_code, display_text ) ),
          assignments:csm_user_role_assignment!fk_csm_ura_user_id (
            role_id, effective_to,
            csm_role_master:role_id ( internal_code )
          )
        `)
        .order('display_name', { ascending: true }).order('user_id').range(from, to));

      type RawUserRow = {
        user_id: number;
        internal_code: string;
        email: string;
        display_name: string;
        preferred_locale: string;
        is_admin: 'Y' | 'N';
        is_active: 'Y' | 'N';
        can_deactivate_users: 'Y' | 'N';
        can_mimic_users: 'Y' | 'N';
        job_description_id: number | null;
        manager_user_id: number | null;
        company_id: number | null;
        division_id: number | null;
        business_unit_id: number | null;
        office_id: number | null;
        job?: { internal_code: string; csm_job_description_tl?: TlRow[] } | null;
        assignments?: { role_id: number; effective_to: string | null; csm_role_master?: { internal_code: string } | null }[];
      };
      const typedRawUsers = (rawUsers ?? []) as RawUserRow[];

      // Resolve manager labels by id - one extra query, no embed required.
      const managerIds = Array.from(new Set(
        typedRawUsers
          .map((u) => u.manager_user_id)
          .filter((id: number | null): id is number => id != null),
      ));
      const managerMap = new Map<number, { display_name: string; email: string }>();
      if (managerIds.length > 0) {
        const { data: mgrRows, error: mErr } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', managerIds);
        if (mErr) throw mErr;
        for (const m of (mgrRows ?? []) as { user_id: number; display_name: string; email: string }[]) {
          managerMap.set(m.user_id, { display_name: m.display_name, email: m.email });
        }
      }

      // Login capability per user: does a Supabase (GoTrue) login exist for this
      // email and is it enabled? Admin-only RPC; non-admins get an empty set so the
      // indicator simply reads "No". Best-effort — a failure leaves it empty.
      const loginByEmail = new Map<string, { has_login: boolean; disabled: boolean }>();
      try {
        const { data: statusRows } = await apiDb
          .rpc<{ email: string; has_login: boolean; login_disabled: boolean }[]>('csm_user_login_status');
        for (const s of statusRows ?? []) {
          if (s.email) loginByEmail.set(s.email.toLowerCase(), { has_login: s.has_login, disabled: s.login_disabled });
        }
      } catch { /* login indicator is best-effort */ }

      const today = new Date().toISOString().slice(0, 10);
      const userRows: UserRow[] = typedRawUsers.map((u) => {
        const tls = u.job?.csm_job_description_tl ?? [];
        const exact = tls.find((t: TlRow) => t.locale_code === locale);
        const fb    = tls.find((t: TlRow) => t.locale_code === 'EN_US') ?? tls[0];
        return {
          user_id: u.user_id,
          internal_code: u.internal_code,
          email: u.email,
          display_name: u.display_name,
          preferred_locale: u.preferred_locale,
          is_admin: u.is_admin,
          is_active: u.is_active,
          can_deactivate_users: u.can_deactivate_users,
          can_mimic_users: u.can_mimic_users,
          job_description_id: u.job_description_id,
          job_description_label: u.job_description_id == null
            ? ''
            : (exact?.display_text ?? fb?.display_text ?? u.job?.internal_code ?? ''),
          manager_user_id: u.manager_user_id,
          manager_label: u.manager_user_id == null
            ? ''
            : (managerMap.get(u.manager_user_id)?.display_name
               ?? managerMap.get(u.manager_user_id)?.email
               ?? `#${u.manager_user_id}`),
          company_id:       u.company_id,
          division_id:      u.division_id,
          business_unit_id: u.business_unit_id,
          office_id:        u.office_id,
          role_codes: (u.assignments ?? [])
            .filter((a) => !a.effective_to || a.effective_to >= today)
            .map((a) => a.csm_role_master?.internal_code)
            .filter(Boolean) as string[],
          can_login: (() => { const s = loginByEmail.get((u.email ?? '').toLowerCase()); return s ? s.has_login && !s.disabled : false; })(),
          has_login: loginByEmail.get((u.email ?? '').toLowerCase())?.has_login ?? false,
        };
      });
      setUsers(userRows);

      // ── Related children for the expand/contract detail panel ───────────────
      // Direct reports come straight from the already-loaded user list (no extra
      // round-trip): every user whose manager_user_id points at this user.
      const reportMap = new Map<number, DirectReportRef[]>();
      for (const u of typedRawUsers) {
        if (u.manager_user_id == null) continue;
        const arr = reportMap.get(u.manager_user_id) ?? [];
        arr.push({ user_id: u.user_id, name: u.display_name || u.email, email: u.email });
        reportMap.set(u.manager_user_id, arr);
      }
      for (const list of reportMap.values()) list.sort((x, y) => x.name.localeCompare(y.name));
      setReportsByUser(reportMap);

      // Roles per user: current assignments (effective_to null or >= today) with the
      // role name resolved through _tl against the active locale (EN_US fallback).
      const { data: rawAssign, error: aErr } = await apiDb
        .from('csm_user_role_assignment')
        .select(`
          user_id, role_id, effective_to,
          csm_role_master:role_id ( internal_code, csm_role_master_tl ( locale_code, display_text ) )
        `);
      if (aErr) throw aErr;

      type RawAssignRow = {
        user_id: number;
        role_id: number;
        effective_to: string | null;
        csm_role_master?: { internal_code: string; csm_role_master_tl?: TlRow[] } | null;
      };
      const roleMap = new Map<number, UserRoleRef[]>();
      const roleSeen = new Map<number, Set<number>>();
      for (const a of (rawAssign ?? []) as RawAssignRow[]) {
        if (a.effective_to && a.effective_to < today) continue;   // closed assignment
        const code = a.csm_role_master?.internal_code ?? '';
        const tls = a.csm_role_master?.csm_role_master_tl ?? [];
        const exact = tls.find((tl: TlRow) => tl.locale_code === locale);
        const fb    = tls.find((tl: TlRow) => tl.locale_code === 'EN_US') ?? tls[0];
        const name  = exact?.display_text ?? fb?.display_text ?? code;
        const seen = roleSeen.get(a.user_id) ?? new Set<number>();
        if (seen.has(a.role_id)) continue;   // de-dupe repeat assignments of one role
        seen.add(a.role_id);
        roleSeen.set(a.user_id, seen);
        const arr = roleMap.get(a.user_id) ?? [];
        arr.push({ role_id: a.role_id, code, name });
        roleMap.set(a.user_id, arr);
      }
      for (const list of roleMap.values()) list.sort((x, y) => x.name.localeCompare(y.name));
      setRolesByUser(roleMap);

      // Pages & permissions per user: the pages each of the user's roles grants
      // access to (csm_role_page_access), unioning the V/A/C/D/S permissions across
      // all of the user's roles. Page names resolve through _tl (§17.B4).
      const { data: rawRpa, error: rpaErr } = await apiDb
        .from('csm_role_page_access')
        .select(`
          role_id, page_id, can_view, can_add, can_change, can_delete, can_signoff,
          csm_page_master:page_id ( internal_code, csm_page_master_tl ( locale_code, display_text ) )
        `);
      if (rpaErr) throw rpaErr;

      type RawRpaRow = {
        role_id: number; page_id: number;
        can_view: boolean; can_add: boolean; can_change: boolean; can_delete: boolean; can_signoff: boolean;
        csm_page_master?: { internal_code: string; csm_page_master_tl?: TlRow[] } | null;
      };
      const permsOf = (r: RawRpaRow): string[] => {
        const p: string[] = [];
        if (r.can_view) p.push('V'); if (r.can_add) p.push('A'); if (r.can_change) p.push('C');
        if (r.can_delete) p.push('D'); if (r.can_signoff) p.push('S');
        return p;
      };
      // role_id -> its granted pages (label + code + perms)
      const rpaByRole = new Map<number, { page_id: number; label: string; code: string; perms: string[] }[]>();
      for (const r of (rawRpa ?? []) as RawRpaRow[]) {
        const code = r.csm_page_master?.internal_code ?? '';
        const tls = r.csm_page_master?.csm_page_master_tl ?? [];
        const exact = tls.find((tl: TlRow) => tl.locale_code === locale);
        const fb    = tls.find((tl: TlRow) => tl.locale_code === 'EN_US') ?? tls[0];
        const arr = rpaByRole.get(r.role_id) ?? [];
        arr.push({ page_id: r.page_id, label: exact?.display_text ?? fb?.display_text ?? code, code, perms: permsOf(r) });
        rpaByRole.set(r.role_id, arr);
      }
      const pagesMap = new Map<number, PageAccessRef[]>();
      for (const [userId, uroles] of roleMap) {
        const byPage = new Map<number, { page_id: number; label: string; code: string; perms: Set<string> }>();
        for (const role of uroles) {
          for (const pg of rpaByRole.get(role.role_id) ?? []) {
            const cur = byPage.get(pg.page_id);
            if (!cur) byPage.set(pg.page_id, { page_id: pg.page_id, label: pg.label, code: pg.code, perms: new Set(pg.perms) });
            else pg.perms.forEach(p => cur.perms.add(p));
          }
        }
        pagesMap.set(userId, [...byPage.values()]
          .map(x => ({ page_id: x.page_id, label: x.label, code: x.code, perms: PERM_ORDER.filter(p => x.perms.has(p)) }))
          .sort((a, b) => a.label.localeCompare(b.label)));
      }
      setPagesByUser(pagesMap);

      // Active roles + localized labels.
      const { data: rawRoles, error: rErr } = await apiDb
        .from('csm_role_master')
        .select(`
          role_id, internal_code, is_active,
          csm_role_master_tl ( locale_code, display_text )
        `)
        .eq('is_active', 'Y')
        .order('internal_code');
      if (rErr) throw rErr;

      type RawRoleRow = { role_id: number; internal_code: string; is_active: 'Y' | 'N'; csm_role_master_tl?: TlRow[] };
      const roleRows: RoleRow[] = (rawRoles ?? []).map((r: RawRoleRow) => {
        const tls: { locale_code: string; display_text: string }[] = r.csm_role_master_tl ?? [];
        const exact = tls.find(t => t.locale_code === locale);
        const fallback = tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
        return {
          role_id: r.role_id,
          internal_code: r.internal_code,
          display_text: exact?.display_text ?? fallback?.display_text ?? r.internal_code,
          is_active: r.is_active,
        };
      });
      setRoles(roleRows);

      // Active job descriptions for the edit drawer picker.
      const { data: rawJobs, error: jErr } = await apiDb
        .from('csm_job_description')
        .select(`
          job_description_id, internal_code,
          csm_job_description_tl ( locale_code, display_text )
        `)
        .eq('is_active', 'Y')
        .order('internal_code');
      if (jErr) throw jErr;

      type RawJobRow = { job_description_id: number; internal_code: string; csm_job_description_tl?: TlRow[] };
      const jobRows: JobOpt[] = (rawJobs ?? []).map((j: RawJobRow) => {
        const tls: { locale_code: string; display_text: string }[] = j.csm_job_description_tl ?? [];
        const exact = tls.find(t => t.locale_code === locale);
        const fb    = tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
        return {
          job_description_id: j.job_description_id,
          internal_code: j.internal_code,
          display_text: exact?.display_text ?? fb?.display_text ?? j.internal_code,
        };
      });
      setJobs(jobRows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // reload is re-created each render; re-run on mount and whenever the active
  // locale changes (so the grid's _tl display text follows a runtime switch).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload();   }, [activeLocale]);

  // User-defined columns / facets an admin placed on this page (runtime, no code).
  const udcGrid = useUdcGrid('csm_user_master', PAGE_CODE, { pkColumn: 'user_id', rows: users as unknown as Record<string, unknown>[] });

  // A derived app's own user attributes, declared as a manifest against this page
  // (src/app/extensions). Inert — no query, no column — when none is registered.
  const ext = useRecordExtensions(PAGE_CODE, { pkColumn: 'user_id' });

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      job:    uniq(users.map(u => u.job_description_label)).map(v => ({ value: v, label: v })),
      locale: uniq(users.map(u => u.preferred_locale)).map(v => ({ value: v, label: localeNameOf(v) })),
      role:   uniq(users.flatMap(u => u.role_codes)).map(v => ({ value: v, label: roleNames[v] ?? v })),
      active: [{ value: 'Y', label: t('common.boolean.active') }, { value: 'N', label: t('common.boolean.inactive') }],
      admin:  [{ value: 'Y', label: t('common.boolean.admin') }, { value: 'N', label: t('common.boolean.standard') }],
    };
  }, [users, t, localeNameOf, roleNames]);

  const facetsActive = facets.job.length + facets.locale.length + facets.role.length + facets.active.length + facets.admin.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0 || ext.hasActiveFilters;
  const clearAll = () => {
    setFilter('');
    setFacets({ job: [], locale: [], role: [], active: [], admin: [] });
    ext.clear();
  };

  const filtered = useMemo(() => {
    const comparator = (a: UserRow, b: UserRow, key: UserSortKey) => {
      if (udcGrid.isUdcKey(key)) return udcGrid.compare(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, key);
      if (ext.isExtKey(key)) return ext.compare(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, key);
      switch (key) {
        case 'role_codes': return compareStrings(a.role_codes.join(','), b.role_codes.join(','));
        default:           return compareStrings((a as unknown as Record<string, unknown>)[key], (b as unknown as Record<string, unknown>)[key]);
      }
    };

    const matchesUdc = (u: UserRow) => udcGrid.matchRow(u as unknown as Record<string, unknown>);
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const matchesFacets = (u: UserRow) =>
      (facets.job.length    === 0 || facets.job.includes(u.job_description_label)) &&
      (facets.locale.length === 0 || facets.locale.includes(u.preferred_locale)) &&
      (facets.role.length   === 0 || u.role_codes.some(c => facets.role.includes(c))) &&
      (facets.active.length === 0 || facets.active.includes(u.is_active)) &&
      (facets.admin.length  === 0 || facets.admin.includes(u.is_admin));

    const base = (!filter.trim()
      ? users
      : users.filter(u => {
          const q = filter.toLowerCase();
          return u.email.toLowerCase().includes(q) ||
            u.display_name.toLowerCase().includes(q) ||
            u.job_description_label.toLowerCase().includes(q) ||
            u.role_codes.some(c => c.toLowerCase().includes(q)) ||
            // An app's extension columns are searchable like native ones.
            ext.searchText(u as unknown as Record<string, unknown>).includes(q);
        })).filter(matchesUdc).filter(matchesFacets)
        .filter(u => ext.matchRow(u as unknown as Record<string, unknown>));

    // Layer the AI's native filter/sort on top of the text filter. When the AI
    // supplied its own ordering, honour it; otherwise keep the header sort.
    if (aiQuery && (aiQuery.filters.length > 0 || aiQuery.sorts.length > 0)) {
      const aiRows = applyAiQuery(base, aiQuery);
      return aiQuery.sorts.length > 0 ? aiRows : sort.sorted(aiRows, comparator);
    }
    return sort.sorted(base, comparator);
  }, [users, filter, facets, sort, aiQuery, udcGrid, ext]);

  const editing = useMemo(
    () => users.find(u => u.user_id === editingId) ?? null,
    [users, editingId],
  );

  // Related-children (expand/contract) helpers - one card per non-empty group.
  const hasRelated = (id: number) =>
    (rolesByUser.get(id)?.length ?? 0) > 0 || (reportsByUser.get(id)?.length ?? 0) > 0 ||
    (pagesByUser.get(id)?.length ?? 0) > 0 || ext.hasRelated(id);

  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    {
      key: 'roles',
      title: t('users.list.related.roles', 'Roles'),
      table: 'csm_role_master', pkColumn: 'role_id',
      items: (rolesByUser.get(id) ?? []).map(r => ({ id: r.role_id, label: r.name, title: r.code })),
    },
    {
      key: 'pages',
      title: t('users.list.related.pages', 'Pages & permissions'),
      table: 'csm_page_master', pkColumn: 'page_id',
      badgeTitles: PERM_TITLES,
      items: (pagesByUser.get(id) ?? []).map(p => ({ id: p.page_id, label: p.label, title: p.code, badges: p.perms })),
    },
    {
      key: 'reports',
      title: t('users.list.related.reports', 'Direct Reports'),
      table: 'csm_user_master', pkColumn: 'user_id',
      items: (reportsByUser.get(id) ?? []).map(r => ({ id: r.user_id, label: r.name, title: r.email })),
    },
    // An app's own child collections follow the native cards (§17.B13).
    ...ext.relatedGroups(id),
  ];

  const toggleActive = async (u: UserRow) => {
    const next = u.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'csm_user_master', 'user_id', u.user_id, {
      is_active: next,
    });
    if (e) { setError(e.message); return; }
    void reload();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        {/* Page header — title + description, actions right (Direction D) */}
        <PageHeader
          title={t('users.list.title', 'Users')}
          description={t('users.list.description', 'People with access to this workspace, their roles and status.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              {canAdd && (
                <button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> {t('users.list.new_user')}
                </button>
              )}
            </>
          }
        />

        {/* Stat strip — value/label pairs, no boxes */}
        <StatStrip
          stats={[
            { value: users.length, label: t('users.stat.total', 'Total users') },
            { value: users.filter(u => u.is_active === 'Y').length, label: t('users.stat.active', 'Active') },
            { value: users.filter(u => u.is_admin === 'Y').length, label: t('users.stat.admins', 'Administrators') },
            { value: users.reduce((n, u) => n + u.role_codes.length, 0), label: t('users.stat.roles', 'Roles assigned') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('users.list.load_failed', "Couldn't load users.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        {/* Content card — toolbar, grid, footer */}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          {/* Toolbar */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('users.list.search_placeholder')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('users.list.columns.job', 'Job')}    options={facetOptions.job}    selected={facets.job}    onChange={v => setFacets(f => ({ ...f, job: v }))} />
            <FacetFilter label={t('users.drawer.field.preferred_locale', 'Locale')} options={facetOptions.locale} selected={facets.locale} onChange={v => setFacets(f => ({ ...f, locale: v }))} />
            <FacetFilter label={t('users.list.columns.roles', 'Roles')}  options={facetOptions.role}   selected={facets.role}   onChange={v => setFacets(f => ({ ...f, role: v }))} />
            <FacetFilter label={t('users.list.columns.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <FacetFilter label={t('users.list.columns.admin', 'Admin')}  options={facetOptions.admin}  selected={facets.admin}  onChange={v => setFacets(f => ({ ...f, admin: v }))} />
            <UdcFacets grid={udcGrid} />
            <ExtensionFacets ext={ext} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              <SmartQueryPopover
                pageCode={PAGE_CODE}
                columns={AI_GRID_SCHEMA}
                permissions={{
                  can_view:   true,
                  can_change: canChange,
                }}
                query={aiQuery}
                onApply={setAiQuery}
                enabled={aiConfig.allow_nlq}
              />
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('users.list.personalize_hint', 'Personalization mode — configure columns below, then click the icon to save and exit.')}
            </div>
          )}

          {/* Grid */}
          <div className="min-h-0 flex-1 overflow-auto">
        {busy && users.length === 0 ? (
          <SkeletonRows rows={8} />
        ) : (
        <PersonalizedGrid componentKey="grid" columns={[...GRID_COLS, ...udcGrid.columns, ...ext.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface2">
                <tr>
                  <th className="w-7 border-b border-csm-line bg-csm-surface2 px-1 py-2.5" title={t('users.list.related.header', 'Related roles, pages & direct reports')}></th>
                  {visibleCols.map(col => (
                    <SortableHeader<UserSortKey>
                      key={col.key}
                      sortKey={col.key as UserSortKey}
                      label={col.header}
                      currentKey={sort.sortKey}
                      currentDir={sort.sortDir}
                      onSort={sort.setSort}
                      style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined}
                    />
                  ))}
                  <th className="border-b border-csm-line bg-csm-surface2 px-3 py-2.5 text-left text-csm-grid-h uppercase text-csm-muted" style={{ width: '1%' }}>{t('users.list.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <Fragment key={u.user_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(u.user_id)}
                        hasChildren={hasRelated(u.user_id)}
                        onToggle={() => detail.toggle(u.user_id)}
                        expandLabel={t('users.list.related.expand_aria', 'Show related records')}
                        collapseLabel={t('users.list.related.collapse_aria', 'Hide related')}
                        tooltip={t('users.list.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'email'                 && u.email}
                        {col.key === 'display_name'          && u.display_name}
                        {col.key === 'job_description_label' && (u.job_description_label || <span className="italic text-csm-watermark">{t('common.none')}</span>)}
                        {col.key === 'preferred_locale'      && <span className="text-csm-ink-soft">{localeNameOf(u.preferred_locale)}</span>}
                        {col.key === 'role_codes'            && (u.role_codes.length === 0 ? <span className="italic text-csm-watermark">{t('common.none')}</span> : u.role_codes.map(c => roleNames[c] ?? c).join(', '))}
                        {col.key === 'is_active'             && <YesNoBadge value={u.is_active} yesLabel={t('common.boolean.active')}   noLabel={t('common.boolean.inactive')} />}
                        {col.key === 'is_admin'              && <YesNoBadge value={u.is_admin}  yesLabel={t('common.boolean.admin')}    noLabel={t('common.boolean.standard')}  />}
                        {col.key === 'can_login'             && <YesNoBadge value={u.can_login ? 'Y' : 'N'} yesLabel={t('users.list.login.yes', 'Yes')} noLabel={t('users.list.login.no', 'No')} />}
                        {udcGrid.isUdcKey(col.key)           && udcGrid.renderCell(u as unknown as Record<string, unknown>, col.key)}
                        {ext.isExtKey(col.key)               && ext.renderCell(u as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      {/* Row actions are gated per TARGET, not per page: a user may be
                          editable but neither deactivatable nor mimicable by this caller.
                          An action they cannot exercise is omitted, not disabled — a
                          greyed row action reads as "temporarily unavailable" when the
                          truth is "not yours to take". */}
                      {canChange ? (() => {
                        const rowActions = [
                          ...(canDeactivateUser(u) ? [{
                            key: 'toggle_active',
                            label: u.is_active === 'Y' ? t('users.list.actions.deactivate') : t('users.list.actions.activate'),
                            onSelect: () => { setActionTarget(u); setActionType(u.is_active === 'Y' ? 'deactivate' : 'activate'); },
                          }] : []),
                          ...(onBeginMimic && canMimicUser(u) ? [{
                            key: 'mimic',
                            label: t('users.list.actions.mimic'),
                            onSelect: () => onBeginMimic(u.user_id),
                            disabled: u.user_id === session.real_admin_id,
                          }] : []),
                        ];
                        return (
                          <span className="inline-flex items-center gap-1">
                            <button onClick={() => setEditingId(u.user_id)}
                              className="inline-block min-w-[3.25rem] rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">
                              {t('users.list.actions.edit')}
                            </button>
                            {rowActions.length > 0 && (
                              <RowActionsMenu label={t('common.row_actions', 'Row actions')} actions={rowActions} />
                            )}
                          </span>
                        );
                      })() : null}
                    </td>
                  </tr>
                  {detail.isOpen(u.user_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(u.user_id)} session={session} onSaved={() => void reload()} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr>
                    <td colSpan={visibleCols.length + 2} className="py-12 text-center">
                      <EmptyState
                        variant={anyFilterActive ? 'no-match' : 'no-data'}
                        title={filter ? t('common.no_results') : t('users.list.empty_state_title')}
                        description={filter ? `${t('common.no_results_for')} "${filter}"` : undefined}
                        action={
                          filter
                            ? <button onClick={() => setFilter('')} className="mt-2 rounded border border-csm-border bg-white px-3 py-1.5 text-csm-sm hover:bg-csm-surface-alt">{t('common.clear_filter')}</button>
                            : canAdd
                              ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">+ {t('users.list.new_user')}</button>
                              : undefined
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>

          {/* Footer — count of shown vs total (client-side grid, no pager) */}
          {!busy && filtered.length > 0 && (
            <div className="flex shrink-0 items-center justify-between border-t border-csm-line bg-csm-surface2 px-3 py-1.5 text-csm-xs text-csm-muted">
              <span>{t('users.list.footer_count', 'Showing {{shown}} of {{total}} users', { shown: filtered.length, total: users.length })}</span>
            </div>
          )}
        </ContentCard>
      </div>

      {editing && canChange && (
        <UserEditModal
          session={session}
          canToggleActive={canDeactivateUser(editing)}
          user={editing}
          roles={roles}
          jobs={jobs}
          allUsers={users}
          orgLookups={orgLookups}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); void reload(); ext.reload(); }}
        />
      )}
      {creating && canAdd && (
        <NewUserModal
          session={session}
          smartPasteEnabled={aiConfig.allow_smart_paste}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); void reload(); ext.reload(); }}
        />
      )}
      {actionTarget && actionType && (
        <ConfirmModal
          title={actionType === 'deactivate' ? t('users.list.confirm.deactivate_title') : t('users.list.confirm.activate_title')}
          message={`${actionType === 'deactivate' ? t('users.list.confirm.deactivate_message') : t('users.list.confirm.activate_message')} "${actionTarget.display_name || actionTarget.email}"?`}
          confirmLabel={actionType === 'deactivate' ? t('users.list.actions.deactivate') : t('users.list.actions.activate')}
          cancelLabel={t('common.cancel')}
          danger={actionType === 'deactivate'}
          onConfirm={async () => {
            const target = actionTarget;
            setActionTarget(null); setActionType(null);
            try {
              await toggleActive(target);
            } catch (e) {
              setError((e as Error).message);
            }
          }}
          onCancel={() => { setActionTarget(null); setActionType(null); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// NEW USER MODAL
// =============================================================================

interface NewUserModalProps {
  session: AuthSession;
  smartPasteEnabled?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function NewUserModal({ session, smartPasteEnabled = false, onClose, onSaved }: NewUserModalProps) {
  const { t } = useTranslation();
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm({
    email:        '',
    password:     '',
    confirm:      '',
    display_name: '',
    locale:       'EN_US',
    enable_login: false,  // default to a business-only record; check to also create a login
  });
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  // The user row is created directly (Common Routine), so there's no record id yet
  // to hang custom fields on — they're set on Edit after creation. Detect placed
  // fields only to show an honest hint.
  const udcInfo = useUdcFormFields('csm_user_master', PAGE_CODE, { pkColumn: 'user_id', pk: null });
  // Extensions ARE wired at create time (unlike UDC above): the Common Routine
  // returns the new row_id, and a manifest field marked immutable_after_create —
  // a business reference — could otherwise never be set at all.
  const extForm = useRecordExtensionForm(PAGE_CODE, null);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    if (!form.email || !form.email.includes('@')) { setError(t('users.login.err_email', 'A valid email is required.')); return; }
    if (form.enable_login) {
      if (!form.password || form.password.length < 8) { setError(t('users.login.err_password_min', 'A password of at least 8 characters is required to create a login.')); return; }
      if (form.password !== form.confirm) { setError(t('users.login.err_password_match', 'Passwords do not match.')); return; }
    }

    setBusy(true); setError(null);
    try {
      // Create the business record (no login yet) via the Common Routine (§2).
      // internal_code is a placeholder UUID; enabling a login rebinds it to the
      // real GoTrue id (csm_provision_user_master trigger, ON CONFLICT email).
      const { row_id, error: e } = await routedInsert(session, 'csm_user_master', {
        internal_code:    crypto.randomUUID(),
        email:            form.email,
        display_name:     form.display_name || form.email.split('@')[0],
        preferred_locale: form.locale || 'EN_US',
        is_active:        'Y',
      });
      if (e) throw new Error(e.message);

      if (form.enable_login) {
        const { error: eL } = await apiDb.authSetLogin({ email: form.email, enabled: true, password: form.password });
        if (eL) throw new Error(eL.message);
      }
      if (row_id != null) await extForm.save(session, row_id);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal
        title={t('users.new_user.title')}
        onClose={handleClose}
        width="md"
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">
              {t('common.cancel')}
            </button>
            <button onClick={() => void save()} disabled={busy || !form.email || (form.enable_login && (!form.password || !form.confirm))}
              className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
              {busy ? t('common.saving') : t('users.new_user.create_button')}
            </button>
          </>
        }
      >
        {smartPasteEnabled && (
          <div className="mb-4">
            <SmartPasteDropzone
              pageCode={PAGE_CODE}
              fields={AI_NEW_USER_FIELDS}
              enabled={smartPasteEnabled}
              onPrefill={(values) => {
                setForm(f => ({
                  ...f,
                  ...(typeof values.email === 'string'            ? { email:        values.email }                                : {}),
                  ...(typeof values.display_name === 'string'     ? { display_name: values.display_name }                        : {}),
                  ...(typeof values.preferred_locale === 'string' ? { locale:       String(values.preferred_locale).toUpperCase() } : {}),
                }));
              }}
            />
          </div>
        )}

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.email')} *</label>
        <input
          type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} autoFocus
          className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md"
        />

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('users.new_user.display_name')}</label>
        <input
          type="text" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
          placeholder={t('users.new_user.display_name_placeholder')}
          className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md"
        />

        {/* System login (Supabase account) — optional; a user can exist without one. */}
        <label className="mb-2 flex items-center gap-2 text-csm-sm font-medium text-csm-ink">
          <input type="checkbox" checked={form.enable_login}
            onChange={e => setForm(f => ({ ...f, enable_login: e.target.checked }))} />
          {t('users.login.enable', 'Can log in to the system')}
        </label>
        {!form.enable_login && (
          <p className="mb-3 text-csm-xs text-csm-muted">
            {t('users.login.create_no_login', 'Created as a business record with no login. You can enable a login later from Edit.')}
          </p>
        )}

        {form.enable_login && (
          <>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.password')} *</label>
            <input
              type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md"
            />

            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('users.new_user.confirm_password')} *</label>
            <input
              type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
              className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md"
            />
          </>
        )}

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('users.drawer.field.preferred_locale')}</label>
        <div className="mb-3">
          <LocaleSelect value={form.locale} onChange={(code) => setForm(f => ({ ...f, locale: code }))} />
        </div>

        <p className="text-csm-xs text-csm-muted">
          {t('users.new_user.hint')}
        </p>
        {udcInfo.hasFields && (
          <p className="mt-1 text-csm-xs text-csm-muted">
            {t('users.new_user.udc_hint', 'Custom fields are set after the user is created — open the user from the list and use Edit.')}
          </p>
        )}

        {error && (
          <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">
            {error}
          </div>
        )}
      </Modal>
      {showDiscard && (
        <ConfirmModal
          title={t('common.discard_changes_title', 'Discard changes?')}
          message={t('common.discard_changes_message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('common.discard', 'Discard')}
          cancelLabel={t('common.keep_editing', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

// =============================================================================
// EDIT MODAL
// =============================================================================

interface UserEditModalProps {
  /**
   * May this caller flip the user's is_active? Required, not defaulted — the
   * list page answers it from the reporting subtree it already holds, the child
   * drill-down from an RPC, and a default would silently pick one for a third.
   */
  canToggleActive: boolean;
  session: AuthSession;
  user: UserRow;
  roles: RoleRow[];
  jobs: JobOpt[];
  allUsers: UserRow[];
  orgLookups: ReturnType<typeof useOrgLookups>;
  onClose: () => void;
  onSaved: () => void;
}

const EDIT_USER_FIELD_DEFS: FieldDef[] = [
  { key: 'display_name',    type: 'text',        label: 'Display Name',    required: true },
  { key: 'preferred_locale',type: 'text',        label: 'Preferred Locale' },
  { key: 'job_description', type: 'select',      label: 'Job Description' },
  { key: 'manager',         type: 'select',      label: 'Manager' },
  { key: 'company',         type: 'select',      label: 'Company' },
  { key: 'division',        type: 'select',      label: 'Division' },
  { key: 'business_unit',   type: 'select',      label: 'Business Unit' },
  { key: 'office',          type: 'select',      label: 'Office' },
  { key: 'is_active',       type: 'boolean',     label: 'Active (Yes / No)' },
  { key: 'roles',           type: 'multiselect', label: 'Roles' },
];
const EDIT_USER_CORE_LAYOUT = toUldCoreLayout(EDIT_USER_FIELD_DEFS);

// Delegated capability flags. Appended to the form ONLY for a CSM.SUPER_ADMIN —
// the server refuses the write from anyone else (CSM.SECURITY.PRIVILEGE_FLAG_SUPER_ADMIN_ONLY),
// so showing them to an ordinary admin would offer a control that can only error.
const EDIT_USER_PRIVILEGE_FIELD_DEFS: FieldDef[] = [
  { key: 'can_deactivate_users', type: 'boolean', label: 'May deactivate their reports' },
  { key: 'can_mimic_users',      type: 'boolean', label: 'May mimic their reports' },
];

function UserEditModal({ session, user, canToggleActive, roles, jobs, allUsers, orgLookups, onClose, onSaved }: UserEditModalProps) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const CK = 'drawer';
  // Custom fields placed on this entity (host = the list page, PAGE_CODE) become
  // first-class FieldRenderer items: they interleave with native fields and inherit
  // the same per-tier personalization (hide / read-only / required / reorder).
  const udcForm = useUdcFormFields('csm_user_master', PAGE_CODE, { pkColumn: 'user_id', pk: user.user_id });
  // A derived app's manifest-declared attributes for this record (§16).
  const extForm = useRecordExtensionForm(PAGE_CODE, user.user_id);
  // Only a super-admin may grant the delegated capabilities (§12).
  const canGrantPrivileges = isSuperAdmin(session);
  // Memoised: the `: []` branch allocates a NEW array every render, so without
  // this the two useMemo hooks below see a changed dependency each time and
  // rebuild the modal's layout and field defs on every keystroke. Both constants
  // are module-level, so the identity is stable for a given canGrantPrivileges.
  const privilegeFieldDefs = useMemo(
    () => (canGrantPrivileges ? EDIT_USER_PRIVILEGE_FIELD_DEFS : []),
    [canGrantPrivileges],
  );
  const editUserCoreLayout = useMemo(
    () => [...EDIT_USER_CORE_LAYOUT, ...toUldCoreLayout(privilegeFieldDefs), ...udcForm.coreLayout],
    [udcForm.coreLayout, privilegeFieldDefs],
  );
  const editUserFieldDefs = useMemo(
    () => [...EDIT_USER_FIELD_DEFS, ...privilegeFieldDefs, ...udcForm.fieldDefs],
    [udcForm.fieldDefs, privilegeFieldDefs],
  );
  const { layout: uldLayout } = useUldLayout('CSM.UM.EDIT_USER.MODAL', editUserCoreLayout);

  const initialValues = {
    display_name:     user.display_name,
    locale:           user.preferred_locale,
    is_active:        user.is_active === 'Y',
    job_id:           user.job_description_id as number | null,
    manager_id:       user.manager_user_id as number | null,
    company_id:       user.company_id as number | null,
    division_id:      user.division_id as number | null,
    business_unit_id: user.business_unit_id as number | null,
    office_id:        user.office_id as number | null,
    role_codes:       [...user.role_codes].sort() as string[],
    can_deactivate_users: user.can_deactivate_users === 'Y',
    can_mimic_users:      user.can_mimic_users === 'Y',
    enable_login:     user.can_login,
    login_password:   '',
    login_password_confirm: '',
  };
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  // Creating a brand-new login needs a password; re-enabling a disabled one does not.
  const loginNeedsPassword = form.enable_login && !user.can_login && !user.has_login;
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const roleOptions: MultiSelectOption[] = roles.map(r => ({
    internal_code: r.internal_code,
    display_text: r.display_text,
    is_active: r.is_active === 'Y',
  }));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      // 1. Patch csm_user_master.
      const { error: e1 } = await routedUpdate(session, 'csm_user_master', 'user_id', user.user_id, {
        display_name:       form.display_name,
        preferred_locale:   form.locale,
        // Omitted when this caller may not change it — same reasoning as the two
        // capability flags below.
        ...(canToggleActive ? { is_active: form.is_active ? 'Y' : 'N' } : {}),
        job_description_id: form.job_id,
        manager_user_id:    form.manager_id,
        company_id:         form.company_id,
        division_id:        form.division_id,
        business_unit_id:   form.business_unit_id,
        office_id:          form.office_id,
        // Omitted entirely for a non-super-admin. The guard trigger only fires
        // on a CHANGE, so resending the current values would be harmless — but
        // not sending them at all keeps the payload honest about what this
        // caller is actually allowed to decide.
        ...(canGrantPrivileges ? {
          can_deactivate_users: form.can_deactivate_users ? 'Y' : 'N',
          can_mimic_users:      form.can_mimic_users      ? 'Y' : 'N',
        } : {}),
      });
      if (e1) throw e1;

      // 2. Diff role assignments.
      //    Removed roles -> set effective_to = today (preserves history).
      //    New roles      -> insert with effective_from = today.
      const today = new Date().toISOString().slice(0, 10);
      const before = new Set(user.role_codes);
      const after  = new Set(form.role_codes);
      const added   = [...after].filter(c => !before.has(c));
      const removed = [...before].filter(c => !after.has(c));

      if (removed.length > 0) {
        const removedIds = roles.filter(r => removed.includes(r.internal_code)).map(r => r.role_id);
        if (removedIds.length > 0) {
          // Multi-row-by-filter has no single-routine form (§2): resolve the open
          // assignment PKs, then route one CHANGE per row (close it, preserve history).
          const { data: openRows, error: eFind } = await apiDb
            .from('csm_user_role_assignment')
            .select('user_role_assignment_id')
            .eq('user_id', user.user_id)
            .in('role_id', removedIds)
            .is('effective_to', null);
          if (eFind) throw eFind;
          for (const row of (openRows ?? []) as { user_role_assignment_id: number }[]) {
            const { error: eRemove } = await routedUpdate(
              session, 'csm_user_role_assignment', 'user_role_assignment_id',
              row.user_role_assignment_id, { effective_to: today },
            );
            if (eRemove) throw eRemove;
          }
        }
      }

      if (added.length > 0) {
        const addedIds = roles.filter(r => added.includes(r.internal_code)).map(r => r.role_id);
        const inserts = addedIds.map(rid => ({
          user_id: user.user_id,
          role_id: rid,
          effective_from: today,
        }));
        const { error: eAdd } = await routedInsertMany(session, 'csm_user_role_assignment', inserts);
        if (eAdd) throw eAdd;
      }

      // 3. Login (Supabase account) — only when the checkbox state changed.
      if (form.enable_login !== user.can_login) {
        if (form.enable_login) {
          if (loginNeedsPassword) {
            if (!form.login_password || form.login_password.length < 8) {
              throw new Error(t('users.login.err_password_min', 'A password of at least 8 characters is required to create a login.'));
            }
            if (form.login_password !== form.login_password_confirm) {
              throw new Error(t('users.login.err_password_match', 'Passwords do not match.'));
            }
          }
          const { error: eL } = await apiDb.authSetLogin({
            email: user.email, enabled: true,
            password: loginNeedsPassword ? form.login_password : undefined,
          });
          if (eL) throw new Error(eL.message);
        } else {
          const { error: eL } = await apiDb.authSetLogin({ email: user.email, enabled: false });
          if (eL) throw new Error(eL.message);
        }
      }

      // 4. A derived app's extension rows, after the host record is safely saved
      //    (see use_record_extension_form for why the two are sequential).
      await extForm.save(session, user.user_id);

      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal
        title={`${t('users.drawer.edit_title')} — ${user.display_name ?? user.email}`}
        onClose={handleClose}
        width="lg"
        headerAction={
          <>
            {session.effective_user.roles.some(r => /ADMIN/i.test(r.internal_code)) && null}
            <PersonalizationToggle />
          </>
        }
        notice={isPersonalizing ? (
          <span className="text-csm-xs text-primary">
            {t('users.drawer.personalize_notice', 'Click 👁 to hide · 🔒 to make read-only · ✳ to mark required · drag to reorder. Click Apply (below) to save.')}
          </span>
        ) : undefined}
        footer={
          <>
            <button
              onClick={handleClose}
              className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft"
            >{t('common.cancel')}</button>
            <button
              onClick={() => void save()}
              disabled={busy}
              className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60"
            >
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </>
        }
      >

        <div className="mb-4 rounded border border-csm-border bg-csm-surface-alt px-3 py-2 text-csm-sm text-csm-ink-soft">
          <div><span className="text-csm-muted">{t('users.drawer.summary.email')}</span> {user.email}</div>
          <div><span className="text-csm-muted">{t('users.drawer.summary.user_id')}</span> {user.user_id}</div>
        </div>

        {/* System login (Supabase account) — a user can exist without one. */}
        <div className="mb-4 rounded border border-csm-border px-3 py-3">
          <label className="flex items-center gap-2 text-csm-sm font-medium text-csm-ink">
            <input
              type="checkbox"
              checked={form.enable_login}
              onChange={e => setForm(f => ({ ...f, enable_login: e.target.checked }))}
            />
            {t('users.login.enable', 'Can log in to the system')}
          </label>
          <p className="mt-1 text-csm-xs text-csm-muted">
            {user.can_login
              ? t('users.login.hint_on', 'This user has an active login. Uncheck to disable it (reversible — the account is kept).')
              : user.has_login
                ? t('users.login.hint_disabled', 'This user has a disabled login. Check to re-enable it with the same password.')
                : t('users.login.hint_off', 'This user has no login. Check and set a temporary password to create one.')}
          </p>
          {loginNeedsPassword && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-csm-xs font-medium text-csm-ink-soft">{t('auth.password', 'Password')} *</label>
                <input type="password" value={form.login_password}
                  onChange={e => setForm(f => ({ ...f, login_password: e.target.value }))}
                  className="h-8 w-full rounded border border-csm-border px-2 text-csm-md" />
              </div>
              <div>
                <label className="mb-1 block text-csm-xs font-medium text-csm-ink-soft">{t('users.login.confirm', 'Confirm password')} *</label>
                <input type="password" value={form.login_password_confirm}
                  onChange={e => setForm(f => ({ ...f, login_password_confirm: e.target.value }))}
                  className="h-8 w-full rounded border border-csm-border px-2 text-csm-md" />
              </div>
            </div>
          )}
        </div>

        <FieldRenderer
          fields={editUserFieldDefs}
          layout={uldLayout}
          columns={1}
          pageCode="CSM.UM.EDIT_USER.MODAL"
          session={session}
          render={(field: FieldLayoutItem) => {
            switch (field.key) {
              case 'display_name': return (
                <PersonalizedField componentKey={CK} fieldKey="display_name" label={t('users.drawer.field.display_name')}
                  readonlyChildren={<div className="mb-3 rounded border border-csm-border bg-csm-surface-alt px-2 py-1 text-csm-md">{form.display_name}</div>}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('users.drawer.field.display_name')}</label>
                  <input type="text" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                    className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none" />
                </PersonalizedField>
              );
              case 'preferred_locale': return (
                <PersonalizedField componentKey={CK} fieldKey="preferred_locale" label={t('users.drawer.field.preferred_locale')}
                  readonlyChildren={<div className="mb-3 rounded border border-csm-border bg-csm-surface-alt px-2 py-1 text-csm-md">{localeName(form.locale, form.locale)}</div>}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('users.drawer.field.preferred_locale')}</label>
                  <div className="mb-3">
                    <LocaleSelect value={form.locale} onChange={(code) => setForm(f => ({ ...f, locale: code }))} />
                  </div>
                </PersonalizedField>
              );
              case 'job_description': return (
                <PersonalizedField componentKey={CK} fieldKey="job_description" label={t('users.drawer.field.job_description')}>
                  <FilteredModalSingleSelect
                    label={t('users.drawer.field.job_description')}
                    options={jobs.map(j => ({ internal_code: j.internal_code, display_text: j.display_text }))}
                    selectedCode={form.job_id == null ? null : jobs.find(j => j.job_description_id === form.job_id)?.internal_code ?? null}
                    onChange={(code) => {
                      if (code === null) setForm(f => ({ ...f, job_id: null }));
                      else { const j = jobs.find(jj => jj.internal_code === code); setForm(f => ({ ...f, job_id: j?.job_description_id ?? null })); }
                    }}
                    placeholder={t('users.drawer.field.job_description_placeholder')}
                    allowClear
                  />
                  <div className="mb-3 mt-1 text-csm-2xs text-csm-muted">{t('users.drawer.field.job_description_hint')}</div>
                </PersonalizedField>
              );
              case 'manager': return (
                <PersonalizedField componentKey={CK} fieldKey="manager" label={t('users.drawer.field.manager')}>
                  <FilteredModalSingleSelect
                    label={t('users.drawer.field.manager')}
                    options={allUsers.filter(u => u.is_active === 'Y' && u.user_id !== user.user_id).map(u => ({
                      internal_code: String(u.user_id),
                      display_text: u.display_name || u.email,
                      description: u.display_name ? u.email : undefined,
                    }))}
                    selectedCode={form.manager_id == null ? null : String(form.manager_id)}
                    onChange={(code) => setForm(f => ({ ...f, manager_id: code === null ? null : Number(code) }))}
                    placeholder={t('users.drawer.field.manager_placeholder')}
                    allowClear
                    clearLabel={t('users.drawer.field.manager_clear_label')}
                  />
                  <div className="mb-3 mt-1 text-csm-2xs text-csm-muted">{t('users.drawer.field.manager_hint')}</div>
                </PersonalizedField>
              );
              case 'company': return (
                <PersonalizedField componentKey={CK} fieldKey="company" label={t('users.drawer.field.company')}>
                  <FilteredModalSingleSelect
                    label={t('users.drawer.field.company')}
                    options={orgLookups.companies.map(c => ({ internal_code: c.internal_code, display_text: c.display_text + (c.is_default === 'Y' ? '  (default)' : '') }))}
                    selectedCode={form.company_id == null ? null : (orgLookups.companies.find(c => c.id === form.company_id)?.internal_code ?? null)}
                    onChange={(code) => setForm(f => ({ ...f, company_id: code == null ? null : (orgLookups.companies.find(c => c.internal_code === code)?.id ?? null) }))}
                    placeholder={t('users.drawer.field.org_placeholder')}
                    allowClear clearLabel={t('users.drawer.field.org_clear_label')}
                  />
                  <div className="mb-3" />
                </PersonalizedField>
              );
              case 'division': return (
                <PersonalizedField componentKey={CK} fieldKey="division" label={t('users.drawer.field.division')}>
                  <FilteredModalSingleSelect
                    label={t('users.drawer.field.division')}
                    options={orgLookups.divisions.map(d => ({ internal_code: d.internal_code, display_text: d.display_text + (d.is_default === 'Y' ? '  (default)' : '') }))}
                    selectedCode={form.division_id == null ? null : (orgLookups.divisions.find(d => d.id === form.division_id)?.internal_code ?? null)}
                    onChange={(code) => setForm(f => ({ ...f, division_id: code == null ? null : (orgLookups.divisions.find(d => d.internal_code === code)?.id ?? null) }))}
                    placeholder={t('users.drawer.field.org_placeholder')}
                    allowClear clearLabel={t('users.drawer.field.org_clear_label')}
                  />
                  <div className="mb-3" />
                </PersonalizedField>
              );
              case 'business_unit': return (
                <PersonalizedField componentKey={CK} fieldKey="business_unit" label={t('users.drawer.field.business_unit')}>
                  <FilteredModalSingleSelect
                    label={t('users.drawer.field.business_unit')}
                    options={orgLookups.business_units.map(b => ({ internal_code: b.internal_code, display_text: b.display_text + (b.is_default === 'Y' ? '  (default)' : '') }))}
                    selectedCode={form.business_unit_id == null ? null : (orgLookups.business_units.find(b => b.id === form.business_unit_id)?.internal_code ?? null)}
                    onChange={(code) => setForm(f => ({ ...f, business_unit_id: code == null ? null : (orgLookups.business_units.find(b => b.internal_code === code)?.id ?? null) }))}
                    placeholder={t('users.drawer.field.org_placeholder')}
                    allowClear clearLabel={t('users.drawer.field.org_clear_label')}
                  />
                  <div className="mb-3" />
                </PersonalizedField>
              );
              case 'office': return (
                <PersonalizedField componentKey={CK} fieldKey="office" label={t('users.drawer.field.office')}>
                  <FilteredModalSingleSelect
                    label={t('users.drawer.field.office')}
                    options={orgLookups.offices.map(o => ({ internal_code: o.internal_code, display_text: o.display_text + (o.is_default === 'Y' ? '  (default)' : '') }))}
                    selectedCode={form.office_id == null ? null : (orgLookups.offices.find(o => o.id === form.office_id)?.internal_code ?? null)}
                    onChange={(code) => setForm(f => ({ ...f, office_id: code == null ? null : (orgLookups.offices.find(o => o.internal_code === code)?.id ?? null) }))}
                    placeholder={t('users.drawer.field.org_placeholder')}
                    allowClear clearLabel={t('users.drawer.field.org_clear_label')}
                  />
                  <div className="mb-3" />
                </PersonalizedField>
              );
              case 'is_active': return (
                /* Shown either way — the status is worth reading even when it is not
                   yours to change — but only operable for a caller who may flip it
                   (super-admin, or can_deactivate_users over one of their reports). */
                <PersonalizedField componentKey={CK} fieldKey="is_active" label={t('users.drawer.field.active_checkbox')}>
                  <label className={'mb-1 flex items-center gap-2 text-csm-sm ' + (canToggleActive ? 'text-csm-ink-soft' : 'text-csm-ink-soft opacity-60')}>
                    <input type="checkbox" checked={form.is_active}
                      disabled={!canToggleActive}
                      onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                      className="h-4 w-4 accent-gray-700" />
                    {t('users.drawer.field.active_checkbox')}
                  </label>
                  {!canToggleActive && (
                    <p className="mb-3 text-csm-xs text-csm-ink-soft">
                      {t('users.drawer.field.active_not_yours', 'Only their manager or a super administrator can change this.')}
                    </p>
                  )}
                  {canToggleActive && <div className="mb-3" />}
                </PersonalizedField>
              );
              case 'can_deactivate_users': return (
                <PersonalizedField componentKey={CK} fieldKey="can_deactivate_users" label={t('users.drawer.field.can_deactivate_users', 'May deactivate their reports')}>
                  <label className="mb-1 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
                    <input type="checkbox" checked={form.can_deactivate_users}
                      onChange={e => setForm(f => ({ ...f, can_deactivate_users: e.target.checked }))}
                      className="h-4 w-4 accent-gray-700" />
                    {t('users.drawer.field.can_deactivate_users', 'May deactivate their reports')}
                  </label>
                  <p className="mb-3 text-csm-xs text-csm-ink-soft">{t('users.drawer.field.privilege_scope_hint', 'Applies only to users beneath this person in the reporting line.')}</p>
                </PersonalizedField>
              );
              case 'can_mimic_users': return (
                <PersonalizedField componentKey={CK} fieldKey="can_mimic_users" label={t('users.drawer.field.can_mimic_users', 'May mimic their reports')}>
                  <label className="mb-1 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
                    <input type="checkbox" checked={form.can_mimic_users}
                      onChange={e => setForm(f => ({ ...f, can_mimic_users: e.target.checked }))}
                      className="h-4 w-4 accent-gray-700" />
                    {t('users.drawer.field.can_mimic_users', 'May mimic their reports')}
                  </label>
                  <p className="mb-3 text-csm-xs text-csm-ink-soft">{t('users.drawer.field.privilege_scope_hint', 'Applies only to users beneath this person in the reporting line.')}</p>
                </PersonalizedField>
              );
              case 'roles': return (
                <PersonalizedField componentKey={CK} fieldKey="roles" label={t('users.drawer.field.roles')}>
                  <FilteredModalMultiSelect
                    label={t('users.drawer.field.roles')}
                    options={roleOptions}
                    selectedCodes={form.role_codes}
                    onChange={(codes) => setForm(f => ({ ...f, role_codes: [...codes].sort() }))}
                    placeholder={t('users.drawer.field.roles_placeholder')}
                  />
                </PersonalizedField>
              );
              default:
                if (udcForm.isUdcKey(field.key)) return (
                  <PersonalizedField componentKey={CK} fieldKey={field.key} label={field.label}
                    readonlyChildren={udcForm.renderControl(field.key, { disabled: true })}>
                    {udcForm.renderControl(field.key)}
                  </PersonalizedField>
                );
                return null;
            }
          }}
        />

        {/* A derived app's own user attributes (src/app/extensions) — renders
            nothing when no extension is registered. */}
        <RecordExtensionFields form={extForm} componentKey={CK} />

        {error && (
          <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">
            {error}
          </div>
        )}
      </Modal>
      {showDiscard && (
        <ConfirmModal
          title={t('common.discard_changes_title', 'Discard changes?')}
          message={t('common.discard_changes_message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('common.discard', 'Discard')}
          cancelLabel={t('common.keep_editing', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// §17.B13 drill-down — this page OWNS the user editor and registers it for
// every other page's related-children panel (module-load side effect; see
// RoleListPage for the rationale).
//
// UserEditModal takes a fully-derived UserRow plus three collections the list
// page assembles. This adapter reproduces that for ONE user: the same job and
// role-assignment embeds, the manager label, and the best-effort login status.
// Getting role_codes wrong here would open a user showing no roles at all —
// which is why it is derived the same way rather than defaulted.
// ---------------------------------------------------------------------------
function UserChildEditor({ session, pk, onClose, onSaved }: ChildEditorProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  // Opened from another page's related-children panel, so there is no reporting
  // line to hand: ask the server about this one user (see use_can_deactivate_user).
  const canToggleActive = useCanDeactivateUser(pk);
  const orgLookups = useOrgLookups(activeLocale);
  const [row, setRow] = useState<UserRow | null>(null);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [jobs, setJobs] = useState<JobOpt[]>([]);
  const [allUsers, setAllUsers] = useState<UserRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [{ data: uData, error: uErr }, { data: rData }, { data: jData }, { data: aData }] = await Promise.all([
          apiDb.from('csm_user_master').select(`
              user_id, internal_code, email, display_name, preferred_locale,
              is_admin, is_active, can_deactivate_users, can_mimic_users,
              job_description_id, manager_user_id,
              company_id, division_id, business_unit_id, office_id,
              job:csm_job_description!fk_csm_um_job_description_id ( internal_code,
                csm_job_description_tl ( locale_code, display_text ) ),
              assignments:csm_user_role_assignment!fk_csm_ura_user_id (
                role_id, effective_to, csm_role_master:role_id ( internal_code ) )
            `).eq('user_id', pk).maybeSingle(),
          apiDb.from('csm_role_master')
            .select('role_id, internal_code, is_active, csm_role_master_tl ( locale_code, display_text )')
            .eq('is_active', 'Y').order('internal_code'),
          apiDb.from('csm_job_description')
            .select('job_description_id, internal_code, csm_job_description_tl ( locale_code, display_text )')
            .eq('is_active', 'Y').order('internal_code'),
          // Manager picker needs only id/name/email (see FilteredModalSingleSelect below).
          apiDb.from('csm_user_master').select('user_id, display_name, email').order('display_name'),
        ]);
        if (cancelled) return;
        if (uErr) throw uErr;

        const pick = (tls: TlRow[] | undefined, fallback: string) => {
          const a = tls ?? [];
          return (a.find(x => x.locale_code === activeLocale)
               ?? a.find(x => x.locale_code === 'EN_US') ?? a[0])?.display_text ?? fallback;
        };
        setRoles(((rData ?? []) as { role_id: number; internal_code: string; is_active: 'Y' | 'N'; csm_role_master_tl?: TlRow[] }[])
          .map(r => ({ role_id: r.role_id, internal_code: r.internal_code, is_active: r.is_active,
                       display_text: pick(r.csm_role_master_tl, r.internal_code) })));
        setJobs(((jData ?? []) as { job_description_id: number; internal_code: string; csm_job_description_tl?: TlRow[] }[])
          .map(j => ({ job_description_id: j.job_description_id, internal_code: j.internal_code,
                       display_text: pick(j.csm_job_description_tl, j.internal_code) })));
        setAllUsers(((aData ?? []) as { user_id: number; display_name: string; email: string }[])
          .map(u => ({ user_id: u.user_id, display_name: u.display_name, email: u.email }) as UserRow));

        if (uData) {
          const u = uData as {
            user_id: number; internal_code: string; email: string; display_name: string;
            preferred_locale: string; is_admin: 'Y' | 'N'; is_active: 'Y' | 'N';
            can_deactivate_users: 'Y' | 'N'; can_mimic_users: 'Y' | 'N';
            job_description_id: number | null; manager_user_id: number | null;
            company_id: number | null; division_id: number | null;
            business_unit_id: number | null; office_id: number | null;
            job?: { internal_code: string; csm_job_description_tl?: TlRow[] } | null;
            assignments?: { role_id: number; effective_to: string | null; csm_role_master?: { internal_code: string } | null }[];
          };
          // Manager label — the same extra round-trip the list makes.
          let managerLabel = '';
          if (u.manager_user_id != null) {
            const { data: m } = await apiDb.from('csm_user_master')
              .select('display_name, email').eq('user_id', u.manager_user_id).maybeSingle();
            const mm = m as { display_name: string; email: string } | null;
            managerLabel = mm?.display_name || mm?.email || `#${u.manager_user_id}`;
          }
          // Login capability: admin-only RPC, best-effort exactly as the list treats it.
          let hasLogin = false, canLogin = false;
          try {
            const { data: statusRows } = await apiDb
              .rpc<{ email: string; has_login: boolean; login_disabled: boolean }[]>('csm_user_login_status');
            const s = (statusRows ?? []).find(x => x.email?.toLowerCase() === u.email.toLowerCase());
            if (s) { hasLogin = s.has_login; canLogin = s.has_login && !s.login_disabled; }
          } catch { /* best-effort, same as the list */ }

          const today = new Date().toISOString().slice(0, 10);
          if (cancelled) return;
          setRow({
            user_id: u.user_id, internal_code: u.internal_code, email: u.email,
            display_name: u.display_name, preferred_locale: u.preferred_locale,
            is_admin: u.is_admin, is_active: u.is_active,
            can_deactivate_users: u.can_deactivate_users, can_mimic_users: u.can_mimic_users,
            job_description_id: u.job_description_id,
            job_description_label: u.job_description_id == null ? ''
              : pick(u.job?.csm_job_description_tl, u.job?.internal_code ?? ''),
            manager_user_id: u.manager_user_id, manager_label: managerLabel,
            company_id: u.company_id, division_id: u.division_id,
            business_unit_id: u.business_unit_id, office_id: u.office_id,
            role_codes: (u.assignments ?? [])
              .filter(a => !a.effective_to || a.effective_to >= today)
              .map(a => a.csm_role_master?.internal_code)
              .filter((c): c is string => !!c),
            can_login: canLogin, has_login: hasLogin,
          });
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pk, activeLocale]);

  if (busy || error || !row) {
    return (
      <Modal title={t('users.drawer.edit_title', 'User')} onClose={onClose} width="lg">
        <div className={'px-1 py-3 text-csm-sm ' + (error ? 'text-csm-danger' : 'text-csm-muted')}>
          {error ?? (busy ? t('common.loading', 'Loading…') : t('common.not_found', 'Record not found.'))}
        </div>
      </Modal>
    );
  }
  return (
    <UserEditModal
      session={session} user={row} canToggleActive={canToggleActive}
      roles={roles} jobs={jobs}
      allUsers={allUsers} orgLookups={orgLookups}
      onClose={onClose} onSaved={onSaved}
    />
  );
}

registerChildEditor('csm_user_master', {
  pageCode: PAGE_CODE,
  title: 'User',
  Editor: UserChildEditor,
});
